import { useEffect } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import GradientButton from "./GradientButton";

const ProjectProfile = ({
  firstName,
  firstDesc,
  firstImg1,
  firstImg2,
  firstLink,
  firstButtonName,
  secondName,
  secondDesc,
  secondImg1,
  secondImg2,
  secondLink,
  secondButtonName,
}) => {
  const controls = useAnimation();
  const [ref, inView] = useInView();

  const fade = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  };

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }

    return () => {
      controls.start("hidden");
    };
  }, [controls, inView]);

  return (
    <motion.div className="container">
      <div className="shell">
        <Profile>
          <div className="first-wrapper">
            <motion.div
              className="image-container ic-1"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 1 }}
              transition={{
                duration: 0.3,
              }}
            >
              <img src={firstImg1} alt="days" className="lower-z" />
              <img src={firstImg2} alt="days" className="higher-z" />
            </motion.div>

            <div className="project-info pi-1">
              <h3> {firstName}</h3>

              <motion.div
                ref={ref}
                animate={controls}
                initial="hidden"
                transition={{
                  duration: 0.8,
                }}
                variants={fade}
              >
                <p>{firstDesc}</p>
              </motion.div>

              <div className="link-btn">
                <GradientButton name={firstButtonName} to={firstLink} />
              </div>
            </div>
          </div>

          <div className="second-wrapper">
            <div className="project-info pi-2">
              <h3>{secondName}</h3>

              <motion.div
                ref={ref}
                animate={controls}
                initial="hidden"
                transition={{
                  duration: 0.8,
                  delay: 0.02,
                }}
                variants={fade}
              >
                <p>{secondDesc}</p>
              </motion.div>

              <div className="link-btn">
                <GradientButton
                  name={secondButtonName}
                  to={secondLink}
                  type={"url"}
                />
              </div>
            </div>

            <motion.div
              className="image-container ic-2"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 1 }}
              transition={{
                duration: 0.3,
              }}
            >
              <img src={secondImg1} alt="days" className="lower-z" />
              <img src={secondImg2} alt="days" className="higher-z" />
            </motion.div>
          </div>
        </Profile>
      </div>
    </motion.div>
  );
};

export default ProjectProfile;

const Profile = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 27rem;

  @media (max-width: 800px) {
    height: 100%;
  }

  .first-wrapper {
    padding-bottom: 2.9rem;
    @media (max-width: 900px) {
      padding-bottom: 1.8rem;
    }
  }

  .second-wrapper {
    padding-top: 2.9rem;

    @media (max-width: 900px) {
      padding-top: 1.8rem;
    }
  }

  .first-wrapper,
  .second-wrapper {
    width: 100%;
    display: flex;
    justify-content: space-between;

    .project-info {
      width: 100%;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .link-btn {
        display: flex;
        justify-content: space-between;
        width: 18rem;
        z-index: 2;

        @media (max-width: 900px) {
          width: 100%;
          justify-content: center;
          text-align: center;

          button {
            margin: 0rem 0.8rem;
          }
        }
      }

      p {
        padding: 1.5rem 0rem;

        @media (max-width: 500px) {
          padding: 1rem 0rem;
        }
      }
    }

    h3 {
      @media (max-width: 370px) {
        font-size: 1rem;
      }
    }

    .pi-2,
    .ic-1 {
      padding-right: 2rem;
    }

    .pi-1,
    .ic-2 {
      padding-left: 2rem;
    }

    .image-container {
      display: flex;
      align-items: center;
      transition: all ease;

      .higher-z {
        z-index: 4;
        transform: perspective(100rem) rotateY(-35deg);
        box-shadow: 7px 6px 0px 0 rgba(109, 232, 251, 0.8);
        margin-left: -4rem;

        &:hover {
          box-shadow: 7px -6px 0px 0 rgba(109, 232, 251, 0.8);
        }

        @media (max-width: 1200px) {
          margin-left: -2.8rem;
        }

        @media (max-width: 600px) {
          margin-left: -2.3rem;
        }

        @media (max-width: 400px) {
          margin-left: -1.7rem;
        }
      }

      .lower-z {
        z-index: 2;
        transform: perspective(100rem) rotateY(35deg);
        box-shadow: -7px 6px 0px 0 rgba(123, 38, 168, 0.8);
        margin-left: -1rem;

        &:hover {
          box-shadow: -7px -6px 0px 0 rgba(123, 38, 168, 0.8);
        }
      }

      img {
        width: 20rem;
        height: 10rem;
        position: relative;
        object-fit: cover;
        border-radius: 0.3rem;
        transition: all 0.5s ease;
        cursor: pointer;

        @media (max-width: 1200px) {
          width: 15rem;
          height: 8rem;
        }

        @media (max-width: 600px) {
          width: 13rem;
          height: 7rem;
        }

        @media (max-width: 400px) {
          width: 10rem;
          height: 5rem;
        }
      }
    }

    @media (max-width: 900px) {
      flex-direction: column;

      .higher-z {
        display: none;
      }

      .pi-2,
      .ic-1 {
        padding-right: 0rem;
      }

      .pi-1,
      .ic-2 {
        padding-left: 0rem;
      }

      .project-info {
        text-align: center;

        p {
          font-size: 0.8rem;
          line-height: 1.3rem;

          @media (max-width: 370px) {
            font-size: 0.7rem;
            line-height: 1.1rem;
          }
        }
      }

      img {
        display: none;
      }
    }
  }
`;
